import { Lock } from 'lucide-react'
import useSWR from 'swr'
import { toast } from 'sonner'
import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/button'
import { useRouter } from '@/routing/navigation'
import { useTranslation } from 'react-i18next'
import { usersApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'

interface LockedFreeGateProps {
  /** Short label for what's gated, e.g. "create studies". */
  action: string
  /** Custom CTA label. Defaults to "Upgrade to unlock". */
  ctaLabel?: string
  /** Where the CTA navigates. Defaults to `/settings/billing/plans`. */
  href?: string
  /** Title override for the full-page variant. */
  title?: string
  /** Description override. Defaults to "Upgrade your plan to {action}." */
  description?: string
  /** Render as an inline blocker (no full-page centering) */
  inline?: boolean
  children?: React.ReactNode
}

/** Full-page or inline empty state shown when a user tries to access a gated feature. */
export function LockedFreeGate({ action, ctaLabel, href = '/settings/billing/plans', title, description, inline = false, children }: LockedFreeGateProps) {
  const { t } = useTranslation('app')
  const router = useRouter()
  const { isOwner, isLoading: ownerLoading } = useCurrentOrg()
  const descText = description ?? t('billing.upgradeToAction', { action })
  const resolvedCta = ctaLabel ?? t('billing.upgradeToUnlock')

  function handleUpgradeClick() {
    if (!isOwner) {
      toast.error(t('billing.ownerOnly'))
      return
    }
    router.push(href)
  }

  if (inline) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 px-4 py-3">
        <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
        <Text size="sm" color="muted" className="flex-1">
          {descText}
        </Text>
        <Button
          size="sm"
          onClick={handleUpgradeClick}
          disabled={ownerLoading}
          data-testid="locked-free-gate-cta"
          className="rounded-[6px] h-[30px] shrink-0"
        >
          {resolvedCta}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center p-12 gap-4 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
        <Lock className="h-6 w-6 text-muted-foreground" />
      </div>
      <div className="flex flex-col gap-1 max-w-xs">
        <Text size="base" weight="medium">{title ?? t('billing.freeViewOnly')}</Text>
        <Text size="sm" color="muted">{descText}</Text>
      </div>
      {children}
      <Button
        onClick={handleUpgradeClick}
        disabled={ownerLoading}
        data-testid="locked-free-gate-cta"
        className="rounded-[6px]"
      >
        {resolvedCta}
      </Button>
    </div>
  )
}

/**
 * Hook to check if the current user is on locked_free tier.
 *
 * Reads from the shared SWR `'profile'` cache (also populated by
 * `UpgradeModalConnected`, with the modal driving a 30s polling refresh).
 * This avoids the "ghost trial" class of bugs where `useAuth().user` is a
 * login-time snapshot that doesn't update when the server transitions an
 * expired trial to `locked_free` mid-session.
 */
export function useIsLockedFree(): boolean {
  const { isAuthenticated } = useAuth()
  // Gate SWR on auth state so the request never fires for unauthenticated
  // share/public-page visitors, where a 401 would otherwise hard-redirect.
  const { data: profile } = useSWR(
    isAuthenticated ? 'profile' : null,
    () => usersApi.getProfile(),
    {
      revalidateOnFocus: false,
      dedupingInterval: 30_000,
    },
  )
  return profile?.subscriptionTier === 'locked_free'
}
